/**
 * TemplatesTab — V2.5-C pre-built report catalog. Renders a 6-card grid of
 * the locked templates and launches TemplateRunnerDialog on click.
 *
 * Data source: `GET /api/reports/templates` (auth-gated by same-origin cookie;
 * server enforces tenant scope). The list is effectively static, so one fetch
 * on mount is enough; no retry loop.
 */

import { useEffect, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { ClipboardCheck, Users, Calendar, Sun, Receipt, Gauge, LayoutGrid } from 'lucide-react'

import type { EventSummary } from '@/contexts/AuthContext'
import type { TemplateSummary } from './types'
import { TemplateRunnerDialog } from './TemplateRunnerDialog'

const ICON_MAP: Record<string, LucideIcon> = {
  'clipboard-check': ClipboardCheck,
  users: Users,
  'calendar-day': Calendar,
  sun: Sun,
  receipt: Receipt,
  gauge: Gauge,
}

interface Props {
  events: EventSummary[]
  currentEventId: number | null
}

export function TemplatesTab({ events, currentEventId }: Props) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState<TemplateSummary | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetch('/api/reports/templates', { credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as { templates?: TemplateSummary[] }
        if (cancelled) return
        setTemplates(Array.isArray(data.templates) ? data.templates : [])
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800"
        data-testid="templates-loading"
      >
        Loading templates…
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        data-testid="templates-error"
      >
        Could not load templates: {error}
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div
        className="rounded-panel border border-dashed border-butler-200 bg-gradient-to-br from-white via-butler-50/60 to-midway-50/40 p-10 text-center shadow-card-soft dark:border-butler-800 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900"
        data-testid="templates-empty"
      >
        <div
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-card-soft ring-1 ring-butler-200 dark:bg-slate-900 dark:ring-butler-700"
          aria-hidden={true}
        >
          <LayoutGrid className="h-6 w-6 text-butler-600 dark:text-butler-200" />
        </div>
        <h2 className="font-display text-lg font-semibold text-butler-800 dark:text-butler-100">
          No templates available
        </h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-soft dark:text-slate-400">
          Template library is empty. Check backend wiring.
        </p>
      </div>
    )
  }

  return (
    <>
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
        data-testid="templates-grid"
      >
        {templates.map((t) => {
          const Icon = (t.icon && ICON_MAP[t.icon]) || LayoutGrid
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t)}
              className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-butler-500 dark:border-slate-700 dark:bg-slate-800"
              data-testid={`template-card-${t.id}`}
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-butler-600 dark:text-butler-300">
                <Icon className="h-4 w-4" aria-hidden={true} />
                Template
              </div>
              <div className="mt-2 truncate text-sm font-semibold text-slate-800 group-hover:text-butler-700 dark:text-slate-100">
                {t.title}
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                {t.description}
              </p>
              <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400">
                <span>
                  {t.required_params.length} param{t.required_params.length !== 1 ? 's' : ''}
                </span>
                {t.pii_policy !== 'none' && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                    PII-stripped
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {active && (
        <TemplateRunnerDialog
          template={active}
          events={events}
          defaultEventId={currentEventId}
          onClose={() => setActive(null)}
        />
      )}
    </>
  )
}
